import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  Add,
  AddCircleOutline,
  CancelOutlined,
  EventAvailableOutlined,
  FileUploadOutlined,
  KeyboardArrowDown,
  LayersOutlined,
  TaskAltOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { defaultAllSelection } from "services/constants";
import { Loader } from "components/Loader";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { ApproveTable, bulkSelectionCheckboxSx } from "pages/TeamZone/Approve/approveUi";
import { assignSalaryPendingHeader, assignSalaryRequestHeader } from "pages/PayRoll/PayRollHeaders";
import { useAuditActor } from "pages/PayRoll/PayInputs/Shared/auditHelpers";
import {
  GlobalBreadcrumb,
  GlobalConfirmDialog,
  sharedStickyFooterBarSx,
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  APP_BAR_OFFSET,
  StatCard,
  designColors,
  designRadii,
  designShell,
  designType,
  designWeights,
  withDesignFont,
} from "services/constants/designTokens";
import { CountTabs, ToneChip, useFilteredRows, useMeasuredHeight } from "../Shared/salaryUi";
import { getSalaryTemplate, getTemplateComponents } from "../SalaryTemplates/salaryTemplateApi";
import {
  APPROVAL_LEVELS,
  emptyAssignForm,
  formatDay,
  formatRupees,
  requestToForm,
  toRequestRow,
  waitingDays,
} from "./assignSalaryData";
import {
  assignRequestAction,
  customComponentRows,
  deleteAssignDraft,
  getAssignChoices,
  getAssignRequest,
  listAssignRequests,
  listNotConfigured,
  saveAssignRequest,
  toAssignBody,
} from "./assignSalaryApi";
import { AssignReviewPanel, AssignSalaryFormPanel } from "./AssignSalaryPanels";

// Payroll > Salary > Assign Salary: first-time salaries, sent as one
// request per group of employees through L1 / L2 approval, on the
// payroll/salary/ assign APIs (assignSalaryApi.js).

const ALL = "all";

const STATUS_CARDS = [
  { key: ALL, title: "All", detail: "Every assignment request", icon: LayersOutlined, accent: designColors.tealDark, tint: designColors.tealTint },
  { key: "Approved", title: "Approved", detail: "Salary assigned on submit", icon: TaskAltOutlined, accent: "#12B76A", tint: "#ECFDF3" },
  { key: "Rejected", title: "Rejected", detail: "Returned with remarks", icon: CancelOutlined, accent: "#DC2626", tint: "#FEF2F2" },
  { key: "Upcoming", title: "Upcoming", detail: "Approved, starts on a future date", icon: EventAvailableOutlined, accent: "#2878FF", tint: "#EAF2FF" },
];
// Assign is approved on submit for now (no L1 / L2 wait), so Approved
// replaces the Pending cards; it holds Upcoming and Applied requests.
const APPROVED_KEYS = ["Upcoming", "Applied"];

const ACTION_BUTTON_WIDTH = 150;

const ACTIONS = {
  Pending: { label: "Review", variant: "outlined" },
  Draft: { label: "Continue", variant: "primary" },
  Rejected: { label: "Edit & resubmit", variant: "danger" },
  Upcoming: { label: "View", variant: "outlined" },
  Applied: { label: "View", variant: "outlined" },
  Cancelled: { label: "View", variant: "outlined" },
};

const sameName = (a, b) => Boolean(a && b) && String(a).split(" (")[0].trim().toLowerCase() === String(b).split(" (")[0].trim().toLowerCase();

export const AssignSalary = (props) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh state per company.
  return <AssignSalaryScreen key={`${domain}:${topbarCompanyId}`} {...props} />;
};

const AssignSalaryScreen = ({ subModuleId }) => {
  const { domain, userScreenAccess, userDetails, topbarCompanyName, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const canEdit = checkEditorPermission(userScreenAccess?.module_details, "Salary Configuration", "Group Salary Component");
  const validCompany =
    Number.isInteger(Number(topbarCompanyId)) && Number(topbarCompanyId) > 0 && Number(topbarCompanyId) !== defaultAllSelection;
  const actor = useAuditActor();

  const [requests, setRequests] = useState([]);
  const [serverCounts, setServerCounts] = useState({});
  const [banner, setBanner] = useState({ count: null, oldest: "" });
  const [available, setAvailable] = useState([]);
  const [availableError, setAvailableError] = useState("");
  const [catalog, setCatalog] = useState([]);
  const [choicesByType, setChoicesByType] = useState({});
  const [templateCache, setTemplateCache] = useState({});
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const requested = useRef({ choices: new Set(), templates: new Set() });

  const [tab, setTab] = useState("requests");
  const [statusCard, setStatusCard] = useState(ALL);
  const [selected, setSelected] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [reviewExpanded, setReviewExpanded] = useState(true);
  const [details, setDetails] = useState({ id: null, request: null, loading: false });
  const [formPanel, setFormPanel] = useState({ open: false, request: null, initial: null });
  // The assign form opens as the full view; the user can collapse it.
  const [formExpanded, setFormExpanded] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [requestFilterRef, requestFilterHeight] = useMeasuredHeight();
  const [pendingFilterRef, pendingFilterHeight] = useMeasuredHeight();

  const requestViews = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "assign_salary_request_saved_views",
    fullHeaders: assignSalaryRequestHeader,
    screenAccess: canEdit,
  });
  const pendingViews = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "assign_salary_pending_saved_views",
    fullHeaders: assignSalaryPendingHeader,
    screenAccess: canEdit,
  });

  // ---- Loading ----

  // Requests tab + Not configured tab. A tab whose call fails stays empty
  // and its error shows as a toast.
  useEffect(() => {
    if (!validCompany) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    Promise.allSettled([
      listAssignRequests(domain, topbarCompanyId, controller.signal),
      listNotConfigured(domain, topbarCompanyId, controller.signal),
    ]).then(([list, people]) => {
      if (controller.signal.aborted) return;
      if (list.status === "fulfilled") {
        setRequests(list.value.requests);
        setServerCounts(list.value.counts);
        setBanner(list.value.notConfigured);
      } else {
        setRequests([]);
        showToast("error", list.reason?.message || "Couldn't load the salary requests.");
      }
      if (people.status === "fulfilled") {
        setAvailable(people.value);
        setAvailableError("");
      } else {
        const message = people.reason?.message || "Couldn't load the employees with no salary.";
        setAvailable([]);
        setAvailableError(message);
        showToast("error", message);
      }
      setLoading(false);
    });
    return () => controller.abort();
  }, [domain, topbarCompanyId, validCompany, refresh]);

  // Components for Customize and the breakdown names. Not critical: the
  // screen works without them.
  useEffect(() => {
    if (!validCompany) return undefined;
    const controller = new AbortController();
    getTemplateComponents(domain, topbarCompanyId, controller.signal)
      .then((items) => { if (!controller.signal.aborted) setCatalog(items); })
      .catch(() => {});
    return () => controller.abort();
  }, [domain, topbarCompanyId, validCompany]);

  // Salary types, templates and payroll groups for one salary type (once).
  const ensureChoices = useCallback(
    (salaryType) => {
      if (!validCompany || !salaryType || requested.current.choices.has(salaryType)) return;
      requested.current.choices.add(salaryType);
      getAssignChoices(domain, topbarCompanyId, salaryType)
        .then((value) => setChoicesByType((current) => ({ ...current, [salaryType]: value })))
        .catch((error) => {
          requested.current.choices.delete(salaryType);
          showToast("error", error.message || "Couldn't load the templates and payroll groups.");
        });
    },
    [domain, topbarCompanyId, validCompany],
  );

  // One template's rows (once), for the preview, checks and Customize.
  const ensureTemplate = useCallback(
    (templateId) => {
      const key = String(templateId ?? "");
      if (!key || requested.current.templates.has(key)) return;
      requested.current.templates.add(key);
      getSalaryTemplate(domain, templateId)
        .then(({ template }) => setTemplateCache((current) => ({ ...current, [key]: { ...template, id: key } })))
        .catch(() => requested.current.templates.delete(key));
    },
    [domain],
  );

  // Templates the panels look up: the choices' templates (no rows yet) with
  // the loaded rows on top.
  const templates = useMemo(() => {
    const map = new Map();
    Object.values(choicesByType).forEach((choices) =>
      choices.templates.forEach((item) =>
        map.set(item.id, { id: item.id, name: item.name, pay_basis: item.pay_basis, active: true, versions: [{ version: item.version, version_id: item.version_id, lines: [] }] }),
      ),
    );
    Object.values(templateCache).forEach((item) => {
      const listed = map.get(item.id);
      const version = item.versions[item.versions.length - 1];
      map.set(item.id, {
        ...listed,
        ...item,
        active: true,
        versions: [{ ...version, version_id: version.version_id ?? listed?.versions[0].version_id }],
      });
    });
    return [...map.values()];
  }, [choicesByType, templateCache]);

  // ---- Requests ----
  const allRows = useMemo(() => requests.map(toRequestRow), [requests]);
  const counts = useMemo(
    () => ({
      [ALL]: serverCounts.all ?? allRows.length,
      Approved: serverCounts.Approved ?? allRows.filter((row) => APPROVED_KEYS.includes(row.status_key)).length,
      Rejected: serverCounts.Rejected ?? allRows.filter((row) => row.status_key === "Rejected").length,
      Upcoming: serverCounts.Upcoming ?? allRows.filter((row) => row.status_key === "Upcoming").length,
    }),
    [allRows, serverCounts],
  );
  const statusRows = useMemo(
    () =>
      statusCard === ALL
        ? allRows
        : statusCard === "Approved"
          ? allRows.filter((row) => APPROVED_KEYS.includes(row.status_key))
          : allRows.filter((row) => row.status_key === statusCard),
    [allRows, statusCard],
  );
  const [visibleRequests, handleRequestFilter] = useFilteredRows(statusRows);

  // ---- Not configured ----
  const pendingRows = useMemo(
    () =>
      available
        .map((employee) => ({ ...employee, joined_label: formatDay(employee.joined), waiting: waitingDays(employee) ?? 0 }))
        .sort((a, b) => b.waiting - a.waiting),
    [available],
  );
  const [visiblePending, handlePendingFilter] = useFilteredRows(pendingRows);
  useEffect(() => {
    setSelected((current) => current.filter((id) => pendingRows.some((row) => row.id === id)));
  }, [pendingRows]);
  const bannerCount = banner.count ?? pendingRows.length;
  const oldestJoined =
    banner.oldest || (pendingRows.length ? pendingRows.filter((row) => row.joined).reduce((a, b) => (!a || b.joined < a ? b.joined : a), "") : "");

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [topbarCompanyName],
  );

  // ---- Review panel ----
  const listRequest = requests.find((request) => request.id === reviewId) ?? null;
  const reviewRequest = details.request && details.id === reviewId ? details.request : listRequest;
  useEffect(() => {
    if (!listRequest) setReviewExpanded(true);
  }, [listRequest]);
  useEffect(() => {
    if (!reviewId) {
      setDetails({ id: null, request: null, loading: false });
      return undefined;
    }
    const controller = new AbortController();
    setDetails({ id: reviewId, request: null, loading: true });
    getAssignRequest(domain, reviewId, controller.signal)
      .then((request) => {
        if (controller.signal.aborted) return;
        setDetails({ id: reviewId, request: { ...listRequest, ...request }, loading: false });
        if (request.template_id) ensureTemplate(request.template_id);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setDetails({ id: reviewId, request: null, loading: false });
        showToast("error", error.message || "Couldn't load the request.");
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, reviewId]);

  // The server decides in the end; these only hide buttons that can't work.
  const mineByName = (request) => sameName(request.created_by, actor.name);
  const canApproveRequest = (request) =>
    canEdit && request?.status === "Pending" && (request.can_approve ?? (request.pending_mine || !mineByName(request)));

  // ---- Actions ----

  // One save / action at a time; errors show as a toast.
  const runAction = async (action) => {
    if (busyRef.current || !validCompany || !canEdit) return { success: false };
    busyRef.current = true;
    setBusy(true);
    try {
      return { success: true, value: await action() };
    } catch (error) {
      showToast("error", error.message);
      return { success: false };
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const reload = () => setRefresh((value) => value + 1);

  const openForm = (request, initial) => {
    setFormExpanded(true);
    setFormPanel({ open: true, request, initial });
  };
  const closeForm = () => setFormPanel((current) => ({ ...current, open: false }));
  const startNew = (ids = []) => {
    const people = ids.map((id) => available.find((employee) => employee.id === id)).filter(Boolean);
    openForm(null, emptyAssignForm(people, choicesByType.Monthly?.groups || []));
  };

  // Draft / Rejected open the form (with the full request); the rest open
  // the review panel.
  const openRequest = async (row) => {
    const request = requests.find((item) => item.id === row.id);
    if (!request) return;
    if ((request.status === "Draft" || request.status === "Rejected") && canEdit && request.can_edit !== false) {
      const result = await runAction(() => getAssignRequest(domain, request.id));
      if (!result.success) return;
      const full = { ...request, ...result.value };
      openForm(full, requestToForm(full));
      return;
    }
    setReviewId(request.id);
  };

  // People the form can name: employees with no salary + the request's own.
  const formPeople = useMemo(() => {
    const own = (formPanel.request?.employees || []).map((row) => ({
      id: row.employee_id, name: row.name, code: row.code, department: row.department, position: row.position, joined: row.joined, group: row.group,
    }));
    const ids = new Set(own.map((person) => String(person.id)));
    return [...own, ...available.filter((person) => !ids.has(String(person.id)))];
  }, [available, formPanel.request]);

  const bodyFor = (form, submit) => {
    const template = templates.find((item) => String(item.id) === String(form.template_id));
    const version = template?.versions[template.versions.length - 1];
    const baseLines = version?.lines || [];
    const listed = choicesByType[form.salary_type]?.templates.find((item) => String(item.id) === String(form.template_id));
    return toAssignBody(form, {
      companyId: topbarCompanyId,
      versionId: version?.version_id ?? listed?.version_id ?? null,
      submit,
      componentsFor: (row) => (row.lines ? customComponentRows(baseLines, row.lines) : null),
    });
  };

  const handleSaveDraft = async (form) => {
    const result = await runAction(() => saveAssignRequest(domain, bodyFor(form, false)));
    if (!result.success) return;
    closeForm();
    reload();
    showToast("success", "Draft saved.");
  };

  // New: one POST with submit. Saved draft / rejected: PUT the changes,
  // then salary-request-action submit (restarts at L1).
  const handleSubmit = async (form) => {
    const resubmit = formPanel.request?.status === "Rejected";
    const result = await runAction(async () => {
      if (!form.request_id) return saveAssignRequest(domain, bodyFor(form, true));
      const id = await saveAssignRequest(domain, bodyFor(form, false));
      await assignRequestAction(domain, id || form.request_id, "submit");
      return id;
    });
    if (!result.success) return;
    closeForm();
    setTab("requests");
    reload();
    showToast("success", `${resubmit ? "Resubmitted" : "Submitted"} and approved. Salary assigned.`);
  };

  const afterReview = (message) => {
    setReviewId(null);
    reload();
    showToast("success", message);
  };

  const handleApprove = async (request, comment) => {
    const final = request.level >= APPROVAL_LEVELS.length;
    const result = await runAction(() => assignRequestAction(domain, request.id, "approve", comment));
    if (!result.success) return;
    afterReview(final ? "Approved. Salaries become effective from each employee's date." : `Approved at L${request.level}. Sent to L${request.level + 1}.`);
  };

  const handleReject = async (request, reason) => {
    const result = await runAction(() => assignRequestAction(domain, request.id, "reject", reason));
    if (!result.success) return;
    afterReview("Request rejected and returned to the submitter.");
  };

  const handleDelete = async () => {
    const target = deleteTarget;
    const result = await runAction(() => deleteAssignDraft(domain, target.id));
    if (!result.success) return;
    setDeleteTarget(null);
    closeForm();
    reload();
    showToast("success", "Draft deleted.");
  };

  // ---- Rendering ----
  const headCell = (offset) => ({ ...sharedTableHeadCellSx, top: APP_BAR_OFFSET + offset, whiteSpace: "nowrap" });
  const bodyCell = { ...sharedTableBodyCellSx, py: 1.25, whiteSpace: "normal" };

  const actionButton = (row) => {
    const action = ACTIONS[row.status_key] || ACTIONS.Applied;
    const label = !canEdit && (row.status_key === "Draft" || row.status_key === "Rejected") ? "View" : action.label;
    // One width for every action button (sized for "Edit & resubmit") so
    // the column lines up.
    const props = { disabled: busy, onClick: (event) => { event.stopPropagation(); openRequest(row); }, sx: { width: ACTION_BUTTON_WIDTH, minWidth: ACTION_BUTTON_WIDTH, minHeight: 36, px: 1.5, whiteSpace: "nowrap" } };
    if (action.variant === "primary" && label !== "View") return <PrimaryButton {...props}>{label}</PrimaryButton>;
    if (action.variant === "danger" && label !== "View") {
      return <SecondaryButton {...props} sx={{ ...props.sx, color: "#B42318", borderColor: "#FCA5A5" }}>{label}</SecondaryButton>;
    }
    return <SecondaryButton {...props}>{label}</SecondaryButton>;
  };

  const renderRequestCell = (row, key) => {
    switch (key) {
      case "request":
        return (
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>{row.request}</Mui.Typography>
            <Mui.Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
              <ToneChip label={row.type} tone={row.type === "Import" ? "purple" : "slate"} />
              <Mui.Typography sx={designType.captionText}>{row.request_detail}</Mui.Typography>
            </Mui.Stack>
          </Mui.Box>
        );
      case "payroll_group":
        return (
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Typography sx={designType.tableBodyCell}>{row.payroll_group}</Mui.Typography>
            <Mui.Typography sx={designType.captionText}>{row.group_detail}</Mui.Typography>
          </Mui.Box>
        );
      case "gross":
        return (
          <Mui.Box sx={{ textAlign: "right" }}>
            {row.each ? (
              <Mui.Typography sx={designType.captionText}>{formatRupees(row.each)} / {row.period} each</Mui.Typography>
            ) : row.employees > 1 ? (
              <Mui.Typography sx={designType.captionText}>Total</Mui.Typography>
            ) : null}
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>
              {formatRupees(row.gross)}{row.period !== "month" ? ` / ${row.period}` : ""}
            </Mui.Typography>
          </Mui.Box>
        );
      case "status":
        return (
          <Mui.Box>
            <ToneChip label={row.status} tone={row.status_tone} dot />
            {row.status_detail ? <Mui.Typography sx={{ ...designType.captionText, mt: 0.5 }}>{row.status_detail}</Mui.Typography> : null}
          </Mui.Box>
        );
      default:
        return row[key] || "–";
    }
  };

  const renderPendingCell = (row, key) => {
    switch (key) {
      case "name":
        return (
          <Mui.Box>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>{row.name}</Mui.Typography>
            <Mui.Typography sx={designType.captionText}>{row.code}</Mui.Typography>
          </Mui.Box>
        );
      case "waiting":
        return <ToneChip label={`${row.waiting} day${row.waiting === 1 ? "" : "s"}`} tone={row.waiting >= 7 ? "red" : "slate"} />;
      default:
        return row[key] || "–";
    }
  };

  const loadingRow = (colSpan) => (
    <Mui.TableRow>
      <Mui.TableCell colSpan={colSpan} sx={{ borderBottom: "none" }}>
        <Loader info="Loading..." minHeight="40vh" />
      </Mui.TableCell>
    </Mui.TableRow>
  );

  const toggle = (id) => setSelected((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  const allVisibleSelected = visiblePending.length > 0 && visiblePending.every((row) => selected.includes(row.id));

  return (
    <ThemeProvider theme={withDesignFont}>
      <Mui.Box sx={{ ...designShell.pageSurface, pb: 2 }}>
        <Mui.Box sx={{ ...designShell.headerRowBase, alignItems: { xs: "flex-start", sm: "center" }, px: 2, pt: 2, mb: 1 }}>
          <Mui.Box sx={{ minWidth: 0 }}>
            <GlobalBreadcrumb trail={["Payroll", "Salary", "Assign Salary"]} />
            <Mui.Typography component="h1" sx={designType.pageTitle}>Assign Salary</Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              First-time salary for employees who don't have one yet. Changes after that go through Salary Revision.
            </Mui.Typography>
          </Mui.Box>
          {canEdit ? (
            <Mui.Button
              startIcon={<Add />}
              endIcon={<KeyboardArrowDown />}
              disabled={busy || loading || !validCompany}
              onClick={(event) => setMenuAnchor(event.currentTarget)}
              aria-haspopup="menu"
              aria-expanded={Boolean(menuAnchor)}
              sx={designShell.primaryButtonBase}
            >
              Assign Salary
            </Mui.Button>
          ) : null}
        </Mui.Box>
        <Mui.Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          elevation={0}
          PaperProps={{ sx: { ...designShell.actionMenuPaper, mt: 0.5 } }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
        >
          {[
            { label: "Assign salary", icon: AddCircleOutline, onClick: () => startNew() },
            { label: "Import from file (not yet available)", icon: FileUploadOutlined, disabled: true },
          ].map((item) => (
            <Mui.MenuItem key={item.label} disabled={item.disabled} sx={designShell.actionMenuItem} onClick={() => { setMenuAnchor(null); item.onClick?.(); }}>
              <Mui.ListItemIcon><item.icon fontSize="small" sx={{ color: designColors.tealDark }} /></Mui.ListItemIcon>
              {item.label}
            </Mui.MenuItem>
          ))}
        </Mui.Menu>

        <Mui.Box sx={{ px: 2 }}>
          {!validCompany ? (
            <Mui.Typography sx={{ ...designType.helperText, mb: 2 }}>Select a company in the top bar to see its salary requests.</Mui.Typography>
          ) : null}
          <CountTabs
            value={tab}
            onChange={setTab}
            tabs={[
              { value: "requests", label: "Requests", count: counts[ALL] },
              { value: "pending", label: "Not configured", count: pendingRows.length },
            ]}
            sx={{ mb: 2, backgroundColor: "transparent" }}
          />

          {tab === "requests" ? (
            <>
              {!loading && bannerCount ? (
                <Mui.Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  alignItems={{ sm: "center" }}
                  justifyContent="space-between"
                  sx={{ backgroundColor: "#FFF7ED", border: "1px solid #F2D680", borderRadius: designRadii.card, px: 2, py: 1.5, mb: 2 }}
                >
                  <Mui.Typography sx={{ fontSize: 14, color: "#A15C00" }}>
                    <b>{bannerCount} employee{bannerCount === 1 ? " has" : "s have"} no salary yet.</b>
                    {oldestJoined ? ` The oldest joined on ${formatDay(oldestJoined)}.` : ""}
                  </Mui.Typography>
                  <SecondaryButton onClick={() => setTab("pending")} sx={{ minWidth: 0, minHeight: 36, backgroundColor: designColors.white }}>View and assign</SecondaryButton>
                </Mui.Stack>
              ) : null}

              <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1.5, mb: 2 }}>
                {STATUS_CARDS.map((card) => (
                  <StatCard
                    key={card.key}
                    icon={card.icon}
                    title={card.title}
                    value={loading ? "–" : counts[card.key]}
                    detail={card.detail}
                    accent={card.accent}
                    tint={card.tint}
                    onClick={() => setStatusCard(card.key)}
                    isActive={statusCard === card.key}
                    activeBorder
                  />
                ))}
              </Mui.Box>

              <Mui.Box ref={requestFilterRef} sx={{ position: "sticky", top: APP_BAR_OFFSET, zIndex: 4, backgroundColor: designColors.white }}>
                <CustomFilter
                  title="Assign Salary"
                  titleOverride={requestViews.titleOverride}
                  filterHeaders={requestViews.visibleHeaders}
                  data={statusRows}
                  filteredData={visibleRequests}
                  onFilteredDataChange={handleRequestFilter}
                  screenAccess={canEdit}
                  hideCount={true}
                  downloadFormats={["excel", "pdf", "csv"]}
                  includeMetadata={true}
                  metadata={reportMetadata}
                  labeledToolbar={true}
                  searchPlaceholder="Search employee, template or request no."
                />
              </Mui.Box>

              <Mui.Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
                <Mui.TableContainer sx={{ width: "100%", overflowX: "visible" }}>
                  <ApproveTable stickyHeader>
                    <Mui.TableHead sx={designShell.tableStickyHead}>
                      <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                        {requestViews.visibleHeaders?.map((head) => (
                          <Mui.TableCell key={head.key} align={head.key === "gross" ? "right" : "left"} sx={headCell(requestFilterHeight)}>
                            {head.key === "gross" ? "Gross / month" : head.name}
                          </Mui.TableCell>
                        ))}
                        <Mui.TableCell align="right" sx={{ ...headCell(requestFilterHeight), width: ACTION_BUTTON_WIDTH + 32 }}>Action</Mui.TableCell>
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {loading ? (
                        loadingRow((requestViews.visibleHeaders?.length ?? 0) + 1)
                      ) : visibleRequests.length === 0 ? (
                        <TableEmptyState dataLength={statusRows} colSpan={(requestViews.visibleHeaders?.length ?? 0) + 1} />
                      ) : (
                        visibleRequests.map((row) => (
                          <Mui.TableRow key={row.id} hover onClick={() => !busy && openRequest(row)} sx={{ cursor: "pointer", ...designShell.tableRowHover, "&:last-child td": { borderBottom: "none" } }}>
                            {requestViews.visibleHeaders.map((head) => (
                              <Mui.TableCell key={head.key} align={head.key === "gross" ? "right" : "left"} sx={bodyCell}>
                                {renderRequestCell(row, head.key)}
                              </Mui.TableCell>
                            ))}
                            <Mui.TableCell align="right" sx={bodyCell}>{actionButton(row)}</Mui.TableCell>
                          </Mui.TableRow>
                        ))
                      )}
                    </Mui.TableBody>
                  </ApproveTable>
                </Mui.TableContainer>
                <Mui.Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ ...sharedStickyFooterBarSx, ...designType.captionText, py: 1.25 }}>
                  <span>Showing {visibleRequests.length} of {statusRows.length} request{statusRows.length === 1 ? "" : "s"}</span>
                  <span>Approval: approved on submit</span>
                </Mui.Stack>
              </Mui.Box>
            </>
          ) : (
            <>
              <Mui.Box ref={pendingFilterRef} sx={{ position: "sticky", top: APP_BAR_OFFSET, zIndex: 4, backgroundColor: designColors.white }}>
                <CustomFilter
                  title="Not configured"
                  titleOverride={pendingViews.titleOverride}
                  filterHeaders={pendingViews.visibleHeaders}
                  data={pendingRows}
                  filteredData={visiblePending}
                  onFilteredDataChange={handlePendingFilter}
                  screenAccess={canEdit}
                  hideCount={true}
                  downloadFormats={["excel", "pdf", "csv"]}
                  includeMetadata={true}
                  metadata={reportMetadata}
                  labeledToolbar={true}
                  searchPlaceholder="Search name or code"
                />
              </Mui.Box>
              {selected.length ? (
                <Mui.Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end" sx={{ ...designShell.sectionCard, borderRadius: 0, mt: 1.5, px: 2, py: 1 }}>
                  <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>{selected.length} selected</Mui.Typography>
                  <Mui.Link component="button" type="button" underline="hover" onClick={() => setSelected([])} sx={{ fontSize: 13, color: designColors.slate600 }}>Clear</Mui.Link>
                  {canEdit ? (
                    <PrimaryButton onClick={() => startNew(selected)} disabled={busy} sx={{ minWidth: 0, minHeight: 38, px: 2 }}>Assign salary to {selected.length}</PrimaryButton>
                  ) : null}
                </Mui.Stack>
              ) : null}
              <Mui.Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
                <Mui.TableContainer sx={{ width: "100%", overflowX: "visible" }}>
                  <ApproveTable stickyHeader>
                    <Mui.TableHead sx={designShell.tableStickyHead}>
                      <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                        <Mui.TableCell padding="checkbox" sx={headCell(pendingFilterHeight)}>
                          <Mui.Checkbox
                            size="small"
                            sx={bulkSelectionCheckboxSx}
                            checked={allVisibleSelected}
                            indeterminate={selected.length > 0 && !allVisibleSelected}
                            onChange={() => setSelected(allVisibleSelected ? [] : visiblePending.map((row) => row.id))}
                            inputProps={{ "aria-label": "Select all employees" }}
                          />
                        </Mui.TableCell>
                        {pendingViews.visibleHeaders?.map((head) => (
                          <Mui.TableCell key={head.key} sx={headCell(pendingFilterHeight)}>{head.name}</Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {loading ? (
                        loadingRow((pendingViews.visibleHeaders?.length ?? 0) + 1)
                      ) : visiblePending.length === 0 ? (
                        <TableEmptyState dataLength={pendingRows} colSpan={(pendingViews.visibleHeaders?.length ?? 0) + 1} />
                      ) : (
                        visiblePending.map((row) => (
                          <Mui.TableRow key={row.id} hover onClick={() => toggle(row.id)} sx={{ cursor: "pointer", ...designShell.tableRowHover, "&:last-child td": { borderBottom: "none" } }}>
                            <Mui.TableCell padding="checkbox" sx={bodyCell} onClick={(event) => event.stopPropagation()}>
                              <Mui.Checkbox size="small" sx={bulkSelectionCheckboxSx} checked={selected.includes(row.id)} onChange={() => toggle(row.id)} inputProps={{ "aria-label": `Select ${row.name}` }} />
                            </Mui.TableCell>
                            {pendingViews.visibleHeaders.map((head) => (
                              <Mui.TableCell key={head.key} sx={bodyCell}>{renderPendingCell(row, head.key)}</Mui.TableCell>
                            ))}
                          </Mui.TableRow>
                        ))
                      )}
                    </Mui.TableBody>
                  </ApproveTable>
                </Mui.TableContainer>
                <Mui.Box sx={{ ...sharedStickyFooterBarSx, ...designType.captionText, py: 1.25 }}>
                  Active employees with no salary. Selecting them opens the Assign Salary flow.
                </Mui.Box>
              </Mui.Box>
            </>
          )}
        </Mui.Box>
      </Mui.Box>

      <AssignReviewPanel
        open={Boolean(reviewRequest)}
        request={reviewRequest}
        catalog={catalog}
        templates={templates}
        canApprove={canApproveRequest(reviewRequest)}
        loading={details.loading}
        busy={busy}
        expanded={Boolean(reviewRequest) && reviewExpanded}
        onToggleExpand={() => setReviewExpanded((value) => !value)}
        onClose={() => setReviewId(null)}
        onApprove={handleApprove}
        onReject={handleReject}
      />

      <AssignSalaryFormPanel
        open={formPanel.open}
        initial={formPanel.initial}
        request={formPanel.request}
        catalog={catalog}
        templates={templates}
        people={formPeople}
        available={available}
        availableError={availableError}
        choicesByType={choicesByType}
        busy={busy}
        expanded={formPanel.open && formExpanded}
        onToggleExpand={() => setFormExpanded((value) => !value)}
        onClose={closeForm}
        onSalaryType={ensureChoices}
        onTemplate={ensureTemplate}
        onSaveDraft={handleSaveDraft}
        onSubmit={handleSubmit}
        onDelete={canEdit ? (request) => setDeleteTarget(request) : null}
      />

      <GlobalConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete draft"
        onCancel={() => { if (!busy) setDeleteTarget(null); }}
        onConfirm={handleDelete}
        confirmLabel="Delete"
        cancelLabel="Keep"
        loading={busy}
        loadingLabel="Deleting..."
        maxWidth="sm"
        danger
      >
        Delete this draft? Its employees go back to Not configured. This can't be undone.
      </GlobalConfirmDialog>
    </ThemeProvider>
  );
};
